import { Link } from "react-router-dom";
import {
    ArrowRight,
    HardHat,
    ShieldCheck,
    ClipboardList,
    CalendarClock,
    MapPin,
    Target,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import useReveal from "../hooks/useReveal";
import { SingleReviewBlock, REVIEWS } from "../components/ClientReviews";
import usePageMeta from "../hooks/usePageMeta";
import useFAQSchema from "../hooks/useFAQSchema";
import { FAQ_ABOUT } from "../data/faqs";
import { site } from "../data/site";

/* ──────────────────────────────────────────────────────────────────
 *  /about — About SRM (Final Corporate Refinement)
 *
 *  The page answers four questions in four movements:
 *    1. Who are SRM?
 *    2. Why are we different?
 *    3. Why trust SRM?  (credibility block)
 *    4. Our philosophy — ASSESS. COMPLY. PROTECT.
 *  + Retained: Meet the Team (approved existing imagery).
 *  + Final CTA (unchanged navigation to /contact).
 *
 *  Branding, colours, typography, spacing and layout system preserved
 *  from the homepage — no new tokens introduced.
 * ────────────────────────────────────────────────────────────────── */

const EYEBROW =
    "font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]";
const GOLD_RULE = "block h-[3px] w-16 bg-srm-gold";

/* === 1. Who are SRM? ============================================== */
function WhoAreSRM() {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="about-hero" className="bg-white">
            <div className="container-srm py-24 lg:py-36">
                <div className="reveal max-w-[880px]">
                    <div className={EYEBROW}>About SRM</div>
                    <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />
                    <h1
                        data-testid="about-hero-headline"
                        className="mt-10 font-display font-bold text-[2.6rem] sm:text-[3.4rem] lg:text-[4.25rem] tracking-[-0.03em] leading-[1.02] text-srm-navy text-balance"
                    >
                        Independent roof &amp;
                        <br />
                        <span className="text-srm-gold">building assessment specialists.</span>
                    </h1>
                    <p className="mt-10 text-[17.5px] lg:text-[20px] text-srm-body leading-[1.7] max-w-[680px]">
                        Strata Roof Management Australia (SRM) provides evidence-based
                        advice to strata communities, body corporate managers and
                        committees throughout Australia, from our base on the Sunshine
                        Coast, Queensland.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* === 2. Why are we different? ===================================== */
function WhyDifferent() {
    const ref = useReveal();
    const POINTS = [
        "Our advice is independent and based solely on the condition of your property.",
        "Every recommendation is guided by what the building genuinely needs, so committees can plan with confidence.",
        "Our reports translate observed condition into practical, prioritised actions — protecting long-term asset value.",
    ];
    return (
        <section
            ref={ref}
            data-testid="about-why-different"
            className="border-t border-srm-line"
            style={{ backgroundColor: "#F5F1EA" }}
        >
            <div className="container-srm py-24 lg:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-[42fr_58fr] gap-14 lg:gap-24 items-start">
                    <div className="reveal lg:sticky lg:top-28">
                        <div className={EYEBROW}>01 &middot; Why We Are Different</div>
                        <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />
                        <h2 className="mt-8 font-display font-bold text-[2.1rem] sm:text-[2.6rem] lg:text-[3rem] tracking-[-0.024em] leading-[1.06] text-srm-navy text-balance">
                            Independent by design.
                            <br />
                            Not by claim.
                        </h2>
                    </div>

                    <div className="reveal">
                        <ul className="space-y-6">
                            {POINTS.map((p, i) => (
                                <li
                                    key={p}
                                    data-testid={`why-different-${i}`}
                                    className="flex items-start gap-5 border-l-2 border-srm-gold pl-6 py-1"
                                >
                                    <span className="text-[16.5px] lg:text-[19px] text-srm-navy leading-[1.55] font-display font-medium tracking-[-0.006em]">
                                        {p}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* === 3. Why trust SRM? — credibility block ======================== */
function WhyTrust() {
    const ref = useReveal();
    const CREDENTIALS = [
        {
            icon: HardHat,
            title: "Builder-level experience",
            body: "Decades of construction and roofing perspective sit behind every assessment.",
        },
        {
            icon: ClipboardList,
            title: "Independent reporting",
            body: "Findings referenced to Australian Standards, NCC and manufacturer requirements.",
        },
        {
            icon: ShieldCheck,
            title: "Professional procurement",
            body: "Defined scopes and comparable tenders — contractors compared on an equal basis.",
        },
        {
            icon: CalendarClock,
            title: "Long-term guardianship",
            body: "Annual assessments and continuous monitoring of the roof asset over time.",
        },
        {
            icon: MapPin,
            title: "Independent decision support",
            body: "Evidence-based guidance that helps committees and managers make confident maintenance and procurement decisions.",
        },
    ];
    return (
        <section
            ref={ref}
            data-testid="about-why-trust"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-24 lg:py-32">
                <div className="max-w-3xl reveal">
                    <div className={EYEBROW}>02 &middot; Why Trust SRM</div>
                    <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[2.1rem] sm:text-[2.6rem] lg:text-[3rem] tracking-[-0.024em] leading-[1.06] text-srm-navy text-balance">
                        Credibility built into every engagement.
                    </h2>
                </div>

                <div className="mt-16 lg:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-6">
                    {CREDENTIALS.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <article
                                key={c.title}
                                data-testid={`trust-credential-${i}`}
                                className="reveal relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-7 lg:p-8 shadow-[0_4px_18px_rgba(15,40,86,0.05)]"
                                style={{ transitionDelay: `${i * 70}ms` }}
                            >
                                <span
                                    aria-hidden
                                    className="pointer-events-none absolute left-0 top-0 h-[3px] w-12 bg-srm-gold"
                                />
                                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-srm-gold/50 text-srm-gold">
                                    <Icon className="h-5 w-5" strokeWidth={1.6} />
                                </span>
                                <h3 className="mt-6 font-display font-bold text-srm-navy text-[1.05rem] lg:text-[1.12rem] tracking-[-0.006em] leading-snug">
                                    {c.title}
                                </h3>
                                <p className="mt-3 text-[13.5px] lg:text-[14px] text-srm-body leading-[1.7]">
                                    {c.body}
                                </p>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* === 4. Our Philosophy — ASSESS. COMPLY. PROTECT. ================= */
function OurPhilosophy() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="about-philosophy"
            className="relative overflow-hidden"
            style={{ backgroundColor: "#0F2856" }}
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -top-24 -right-24 h-[440px] w-[440px] rounded-full"
                style={{
                    background:
                        "radial-gradient(circle, rgba(201,165,92,0.10) 0%, rgba(201,165,92,0) 70%)",
                }}
            />
            <div className="container-srm relative py-24 lg:py-36">
                <div className="max-w-4xl reveal">
                    <div className="font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]">
                        03 &middot; Our Philosophy
                    </div>
                    <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />

                    <h2
                        data-testid="philosophy-heading"
                        className="mt-10 font-display font-bold text-white text-[2.6rem] sm:text-[3.6rem] lg:text-[4.75rem] tracking-[-0.03em] leading-[1.02] text-balance"
                    >
                        Assess.
                        <br />
                        Comply.
                        <br />
                        <span className="text-srm-gold">Protect.</span>
                    </h2>

                    <p className="mt-12 text-[17px] lg:text-[19.5px] text-white/80 leading-[1.7] max-w-[720px]">
                        Our role is to provide independent advice that helps clients
                        make informed decisions, protect their assets and plan for
                        the future with confidence.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* === 5. Meet the Team — Andy, Amanda, Joe ========================= */
function MeetTheTeam() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="about-team"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-24 lg:py-32">
                <div className="max-w-3xl reveal">
                    <div className={EYEBROW}>04 &middot; The People</div>
                    <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[2.1rem] sm:text-[2.6rem] lg:text-[3rem] tracking-[-0.024em] leading-[1.06] text-srm-navy text-balance">
                        The team behind every engagement.
                    </h2>
                </div>

                <div className="mt-14 lg:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
                    {site.team.map((m, i) => (
                        <article
                            key={m.name}
                            data-testid={`about-team-card-${i}`}
                            className="reveal group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(15,40,86,0.14)]"
                            style={{ transitionDelay: `${i * 80}ms` }}
                        >
                            <span
                                aria-hidden
                                className="pointer-events-none absolute left-0 top-0 h-[3px] w-16 bg-srm-gold transition-all duration-500 group-hover:w-full z-10"
                            />
                            <div className="relative w-full aspect-[4/5] overflow-hidden bg-[#EEF1F5]">
                                <img
                                    src={m.image}
                                    alt={`${m.name} \u2014 ${m.role}`}
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                                    style={{ objectPosition: "50% 22%" }}
                                    loading="lazy"
                                />
                            </div>
                            <div className="flex flex-1 flex-col px-7 lg:px-8 pt-7 pb-8">
                                <h3
                                    data-testid={`about-team-name-${i}`}
                                    className="font-display font-bold text-srm-navy text-[1.32rem] lg:text-[1.45rem] tracking-[-0.012em] leading-tight"
                                >
                                    {m.name}
                                </h3>
                                <div className="mt-3 font-display font-semibold tracking-[0.2em] uppercase text-srm-gold text-[11px]">
                                    {m.role}
                                </div>
                                <span aria-hidden className="mt-5 block h-[2px] w-10 bg-srm-gold" />
                                <p className="mt-6 text-[14.5px] lg:text-[15px] text-srm-body leading-[1.75]">
                                    {m.bio}
                                </p>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="mt-14 flex justify-center">
                    <Link
                        to="/team"
                        data-testid="about-team-link"
                        className="group inline-flex items-center gap-2 rounded-sm bg-transparent text-srm-navy text-[12px] font-semibold tracking-[0.2em] uppercase px-9 h-[52px] border border-srm-navy/25 hover:bg-srm-navy hover:text-white hover:border-srm-navy hover:-translate-y-0.5 transition-all duration-300"
                    >
                        Full Team Details
                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                    </Link>
                </div>
            </div>
        </section>
    );
}

/* === 5b. Industry Affiliation ==================================== */
function IndustryAffiliation() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="about-affiliation"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-16 lg:py-20">
                <div className="max-w-3xl mx-auto reveal text-center">
                    <div className={EYEBROW}>Professional affiliation</div>
                    <span aria-hidden className={`mx-auto mt-5 ${GOLD_RULE}`} />
                    <div
                        data-testid="about-affiliation-logo"
                        className="mt-10 flex justify-center"
                    >
                        <img
                            src="/assets/mbq-proud-member.png"
                            alt="Master Builders Queensland Proud Member"
                            className="h-auto w-full max-w-[280px] lg:max-w-[320px]"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                    <p className="mt-8 mx-auto max-w-[520px] text-[14px] lg:text-[14.5px] text-srm-body leading-[1.75]">
                        SRM is a proud member of Master Builders Queensland.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* === 6. Final CTA ================================================= */
function FinalCTA() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="about-final-cta"
            className="relative overflow-hidden bg-srm-navyDark"
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -bottom-32 -left-24 h-[380px] w-[380px] rounded-full"
                style={{
                    background:
                        "radial-gradient(circle, rgba(201,165,92,0.08) 0%, rgba(201,165,92,0) 70%)",
                }}
            />
            <div className="container-srm relative py-24 lg:py-32">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <Target className="mx-auto h-8 w-8 text-srm-gold" strokeWidth={1.4} />
                    <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.5rem] lg:text-[3rem] tracking-[-0.024em] leading-[1.1] text-white text-balance">
                        Speak with the SRM team.
                    </h2>
                    <div className="mt-12 flex justify-center">
                        <Link
                            to="/contact"
                            data-testid="about-cta-primary"
                            className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[12px] font-semibold tracking-[0.2em] uppercase px-10 h-[56px] border border-srm-gold shadow-[0_10px_30px_rgba(201,165,92,0.30)] hover:bg-srm-goldDark hover:border-srm-goldDark hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(201,165,92,0.40)] transition-all duration-300"
                        >
                            Speak With The Team
                            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* === Page composition ============================================= */
export default function About() {
    usePageMeta({
        title: "About SRM \u2014 Strata Roof Management Australia",
        description:
            "Strata Roof Management Australia (SRM) provides independent roof and external-building assessment advice for strata communities, body corporate managers and committees. Based on the Sunshine Coast, Queensland, serving clients Australia-wide.",
        path: "/about",
        breadcrumbs: [
            { name: "Home", url: "/" },
            { name: "About SRM", url: "/about" },
        ],
    });
    useFAQSchema("about", FAQ_ABOUT);

    return (
        <div data-testid="about-page" className="min-h-screen bg-white">
            <Navbar />
            <main id="main">
                <WhoAreSRM />
                <WhyDifferent />
                <WhyTrust />
                <OurPhilosophy />
                <MeetTheTeam />
                <SingleReviewBlock
                    review={REVIEWS.hooper}
                    heading="From the people we support"
                    testid="about-review"
                />
                <IndustryAffiliation />
                <FinalCTA />
            </main>
            <Footer />
        </div>
    );
}
